import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import http, {
  getProfile,
  postAccessGateway,
  postAuthToken,
  putCommercialCommunications,
  putTermsAndConditionsAccepted,
} from 'utils/network/http';
import {
  identifyToSegment,
  sendEventToSegment,
} from 'utils/functions/Tracking';
import TRACKING_EVENTS from 'utils/constants/trackingEvents';
import useDriverStatus from 'hooks/useDriverStatus/useDriverStatus';
import { useTranslation } from 'react-i18next';
import { setUserContext } from '../../../src/sentry';

const authContext = createContext();

export function ProvideAuth({ children }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

export const useAuth = () => {
  return useContext(authContext);
};

// Provider hook that creates auth object and handles state
function useProvideAuth() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [shouldSetPassword, setShouldSetPassword] = useState(false);
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const { isDriverActive } = useDriverStatus({ profile });
  const termsAccepted = profile?.user?.terms_and_conditions_accepted;
  const commercialCommunicationsAccepted =
    profile?.user?.commercial_communications_accepted;
  const { i18n } = useTranslation();

  const commonLogin = useCallback((data) => {
    const { access_token, refresh_token, dispatchee } = data;
    if (dispatchee === false) {
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      setAccessToken(access_token);
      sendEventToSegment(TRACKING_EVENTS.LOGIN);
    } else {
      setError(true);
    }
  }, []);

  const signin = useCallback(
    async (username, password) => {
      try {
        setLoading(true);
        setError(false);
        const response = await postAuthToken(username, password);
        commonLogin(response.data);
        setIsAutoLogin(false);
      } catch (e) {
        console.error(`Error: Auth token ${e}`);
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [commonLogin]
  );

  const signInWithToken = useCallback(
    async (token) => {
      try {
        setLoading(true);
        setError(false);
        const response = await postAccessGateway(token);
        commonLogin(response.data);
        setIsAutoLogin(true);
      } catch (e) {
        console.error(`Error: SignInWithToken ${e}`);
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [commonLogin]
  );

  const signout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setAccessToken(null);
    setProfile(null);
    sendEventToSegment(TRACKING_EVENTS.LOGOUT);
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      if (!accessToken) return false;
      http.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      const response = await getProfile();
      const data = response.data;
      setProfile(data);
      setShouldSetPassword(!data.user.is_pwd_set_by_user);
      i18n.changeLanguage(data.user.preferences?.language || null);
      identifyToSegment({
        user: {
          uuid: data.driver.uuid,
        },
      });
      setUserContext({
        email: data.user.username,
        id: data.driver.id,
        name: data.driver.contact_name,
      });
    } catch (e) {
      console.error('Error getProfile', e);
      if (e.response.status === 400) {
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  }, [accessToken, i18n]);

  const acceptTermsAndConditions = async () => {
    try {
      setLoading(true);
      if (!profile) return false;
      await putTermsAndConditionsAccepted();
      await fetchProfile();
    } catch (e) {
      console.error('Error acceptTermsAndConditions', e);
    } finally {
      setLoading(false);
    }
  };

  const answerCommercialCommunications = async (accepted = false) => {
    try {
      setLoading(true);
      if (!profile) return false;
      await putCommercialCommunications(accepted);
      await fetchProfile();
    } catch (e) {
      console.error('Error answerCommercialCommunications', e);
    } finally {
      setLoading(false);
    }
  };

  const isLogged = !!localStorage.getItem('access_token');

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token && !profile && !loading) {
      setAccessToken(token);
      fetchProfile();
    }
  }, [fetchProfile, profile, loading]);

  // Return the profile object and auth methods
  return {
    profile,
    termsAccepted,
    commercialCommunicationsAccepted,
    signin,
    signout,
    signInWithToken,
    loading,
    error,
    accessToken,
    shouldSetPassword,
    isAutoLogin,
    isDriverActive,
    acceptTermsAndConditions,
    answerCommercialCommunications,
    setProfile,
    isLogged,
  };
}
